import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  useGetMemberHistory,
  useListCheckins,
  useUpdateCheckin,
  getGetMemberHistoryQueryKey,
  getListCheckinsQueryKey,
  getGetCurrentUserQueryKey,
} from "@workspace/api-client-react";
import type { HistoryItem } from "@workspace/api-client-react";
import { cn } from "@/lib/utils";
import { localDateStr } from "@/lib/localDate";
import { useRunnerView } from "@/hooks/useRunnerView";
import { CheckinHeatmap } from "@/pages/member-detail";

// WI#23 (S2-S4) — My History, from the approved prototype. Month filter plus numbered pages
// (Gerald: "show more … never works well in practice"). Edit is offered within the server's grace
// window (editWindowHours from the API), so the app never offers an edit the server will refuse.

const RUN_TYPES = ["Easy Run", "Tempo", "Long Run", "Intervals", "Cross-train", "Other"] as const;

function monthLabel(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, 1)).toLocaleDateString([], { month: "long", timeZone: "UTC" });
}

function dateLabel(d: string): string {
  return new Date(`${d}T12:00:00Z`).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
}

function EditModal({ item, onClose }: { item: HistoryItem; onClose: () => void }) {
  const queryClient = useQueryClient();
  const update = useUpdateCheckin();
  const c = item.checkin;
  const [status, setStatus] = useState<"crushed" | "skipped">(c.status);
  const [note, setNote] = useState(c.note ?? "");
  const [runType, setRunType] = useState<string | null>(c.sessionType ?? null);
  const [miles, setMiles] = useState(c.miles != null ? String(c.miles) : "");
  const [error, setError] = useState("");

  const save = () => {
    const m = miles.trim() === "" ? null : Number.parseFloat(miles);
    if (m !== null && (Number.isNaN(m) || m < 0 || m > 200)) {
      setError("Distance should be a number of miles between 0 and 200.");
      return;
    }
    update.mutate(
      { id: c.id, data: { status, note: note.trim() || null, sessionType: runType, miles: m } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetMemberHistoryQueryKey(c.memberId) });
          queryClient.invalidateQueries({ queryKey: getListCheckinsQueryKey({ memberId: c.memberId }) });
          queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() });
          onClose();
        },
        onError: () => setError("That didn't save. Runs lock 24 hours after logging."),
      },
    );
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur flex items-center justify-center z-50 p-4">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="pr-modal">
        <h2 className="font-display text-xl font-bold mb-6">Edit {dateLabel(c.checkinDate)}</h2>
        <div className="space-y-4">
          <div>
            <div className="pr-label">What did you do?</div>
            <div className="flex flex-wrap gap-2">
              {RUN_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setRunType(runType === t ? null : t)}
                  className={cn(
                    "px-3 py-2 rounded-full text-sm border",
                    runType === t ? "bg-foreground text-background border-foreground font-semibold" : "border-border",
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="pr-label" htmlFor="edit-miles">Distance (optional)</label>
            <input id="edit-miles" className="pr-field" inputMode="decimal" value={miles} onChange={(e) => setMiles(e.target.value)} />
          </div>
          <div>
            <div className="pr-label">How did it go?</div>
            <div className="grid grid-cols-2 gap-3">
              {(["crushed", "skipped"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={cn(
                    "rounded p-3 text-left border-2 font-display font-bold",
                    status === s ? (s === "crushed" ? "border-foreground bg-primary" : "border-foreground bg-card") : "border-border text-muted-foreground",
                  )}
                >
                  {s === "crushed" ? "CRUSHED IT" : "SKIPPED"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="pr-label" htmlFor="edit-note">Note</label>
            <input id="edit-note" className="pr-field" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-border rounded py-2.5 text-sm">
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={update.isPending}
              className="flex-1 bg-primary text-primary-foreground rounded py-2.5 text-sm font-semibold disabled:opacity-50"
            >
              {update.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function Row({ item, onEdit, editWindowHours }: { item: HistoryItem; onEdit: () => void; editWindowHours: number }) {
  const c = item.checkin;
  const planned = item.planned;
  const did = c.sessionType ?? (planned && planned.sessionType !== "Rest" ? planned.sessionType : "Run");
  const dist = c.miles ?? (!c.sessionType && planned ? planned.targetMiles : null);
  const swapped = !!c.sessionType && !!planned && planned.sessionType !== "Rest" && planned.sessionType !== c.sessionType;
  const selfLogged = !!c.sessionType && !planned;
  const editable = Date.now() - new Date(c.createdAt).getTime() < editWindowHours * 60 * 60 * 1000;
  return (
    <div className="flex items-start gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 border-t border-border">
      <span className={cn("w-2.5 h-2.5 rounded-full mt-1.5 shrink-0", c.status === "crushed" ? "bg-primary" : "bg-red-500")} />
      <div className="w-20 sm:w-24 shrink-0 text-xs font-mono text-muted-foreground mt-0.5">{dateLabel(c.checkinDate)}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm">
          <b>{did}</b>
          {dist ? (
            <span className="ml-1.5 bg-primary text-xs font-mono font-bold px-1.5 py-0.5 rounded-sm">{dist} mi</span>
          ) : null}
          <span className="ml-2 text-muted-foreground">{c.status === "crushed" ? "Crushed it" : "Skipped"}</span>
        </div>
        {swapped && planned && (
          <div className="text-xs text-muted-foreground mt-0.5">
            planned {planned.sessionType}
            {planned.targetMiles ? ` · ${planned.targetMiles} mi` : ""}
          </div>
        )}
        {selfLogged && <div className="text-xs text-muted-foreground mt-0.5">logged by you, nothing planned</div>}
        {c.note && <div className="text-sm text-muted-foreground italic mt-0.5">"{c.note}"</div>}
      </div>
      {editable && (
        <button type="button" onClick={onEdit} className="text-xs font-semibold underline underline-offset-2 shrink-0">
          Edit
        </button>
      )}
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-card border border-border rounded p-4">
      <div className="font-display text-2xl sm:text-3xl font-bold">{value}</div>
      <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

export default function History() {
  const { me, isLoading } = useRunnerView();
  const thisMonth = localDateStr().slice(0, 7);
  const [month, setMonth] = useState<string | undefined>(thisMonth);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<HistoryItem | null>(null);
  const memberId = me?.id ?? 0;
  const params = { month, page };
  const { data: history, isLoading: historyLoading } = useGetMemberHistory(memberId, params, {
    query: { enabled: !!me, queryKey: getGetMemberHistoryQueryKey(memberId, params) },
  });
  const monthParams = { month: thisMonth, page: 1 };
  const { data: thisMonthHistory } = useGetMemberHistory(memberId, monthParams, {
    query: { enabled: !!me, queryKey: getGetMemberHistoryQueryKey(memberId, monthParams) },
  });
  const { data: allCheckins } = useListCheckins(
    { memberId },
    { query: { enabled: !!me, queryKey: getListCheckinsQueryKey({ memberId }) } },
  );

  if (isLoading || !me) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading…</div>;

  const pages = history ? Math.max(1, Math.ceil(history.total / history.pageSize)) : 1;
  const months = history?.months ?? [];
  const filterMonths = months.includes(thisMonth) ? months : [thisMonth, ...months];
  const first = history && history.total > 0 ? (history.page - 1) * history.pageSize + 1 : 0;
  const last = history ? Math.min(history.page * history.pageSize, history.total) : 0;
  const chooseMonth = (m: string | undefined) => {
    setMonth(m);
    setPage(1);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Your runs</div>
        <h1 className="font-display text-3xl font-bold mt-1">My History</h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat value={`${me.currentStreak}d`} label="Current streak" />
        <Stat value={`${me.longestStreak}d`} label="Longest" />
        <Stat value={me.adherencePct ? `${me.adherencePct}%` : "—"} label="Adherence" />
        <Stat value={String(thisMonthHistory?.total ?? 0)} label="Check-ins this month" />
      </div>

      <div className="bg-background border border-border rounded p-4 sm:p-5">
        <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground mb-3">Last 12 weeks</h2>
        <CheckinHeatmap checkins={allCheckins ?? []} />
      </div>

      <div className="bg-background border border-border rounded">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 sm:px-5 py-4">
          <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">Every run</h2>
          <div className="flex flex-wrap gap-1.5">
            {filterMonths.slice(0, 3).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => chooseMonth(m)}
                className={cn(
                  "px-3 py-1.5 rounded text-sm",
                  month === m ? "bg-foreground text-background font-semibold" : "border border-border",
                )}
              >
                {monthLabel(m)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => chooseMonth(undefined)}
              className={cn("px-3 py-1.5 rounded text-sm", month === undefined ? "bg-foreground text-background font-semibold" : "border border-border")}
            >
              All
            </button>
          </div>
        </div>

        {historyLoading ? (
          <div className="px-5 py-6 text-sm text-muted-foreground font-mono animate-pulse border-t border-border">Loading…</div>
        ) : !history || history.items.length === 0 ? (
          <div className="px-5 py-6 text-sm text-muted-foreground border-t border-border">
            {month ? `No check-ins in ${monthLabel(month)} yet.` : "No check-ins yet. Your first one starts your streak."}
          </div>
        ) : (
          history.items.map((item) => (
            <Row key={item.checkin.id} item={item} editWindowHours={history.editWindowHours} onEdit={() => setEditing(item)} />
          ))
        )}

        {history && history.total > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 sm:px-5 py-3 border-t border-border">
            <span className="text-xs font-mono text-muted-foreground">
              Showing {first}–{last} of {history.total} {month ? `in ${monthLabel(month)}` : "check-ins"}
            </span>
            {pages > 1 && (
              <div className="flex items-center gap-1.5 text-sm">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="px-2.5 py-1.5 border border-border rounded disabled:text-muted-foreground/50"
                >
                  ‹ Prev
                </button>
                {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n)}
                    className={cn("px-2.5 py-1.5 rounded", n === page ? "bg-foreground text-background font-semibold" : "border border-border")}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={page >= pages}
                  onClick={() => setPage(page + 1)}
                  className="px-2.5 py-1.5 border border-border rounded disabled:text-muted-foreground/50"
                >
                  Next ›
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        You can edit a run for {history?.editWindowHours ?? 24} hours after logging it. After that it's locked, so your
        squad's board stays honest.
      </p>

      {editing && <EditModal item={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
